import {
  AlarmClock,
  Anchor,
  Apple,
  Archive,
  Armchair,
  AtSign,
  Award,
  Baby,
  Backpack,
  Banknote,
  Battery,
  Bed,
  Beer,
  Bell,
  Bike,
  Bird,
  Bluetooth,
  Book,
  Bookmark,
  BookOpen,
  Briefcase,
  Bug,
  Building,
  Building2,
  Cake,
  Calendar,
  Camera,
  Car,
  Carrot,
  Castle,
  Cat,
  Cherry,
  Church,
  Circle,
  Clipboard,
  ClipboardList,
  Clock,
  Cloud,
  CloudRain,
  Code,
  Coffee,
  Coins,
  Compass,
  Cpu,
  CreditCard,
  Crown,
  Database,
  Diamond,
  Dice5,
  Dog,
  DollarSign,
  Droplet,
  Dumbbell,
  Egg,
  Eye,
  EyeOff,
  Factory,
  File,
  Files,
  FileText,
  Film,
  Fish,
  Flag,
  Flame,
  Flower,
  Flower2,
  Folder,
  FolderOpen,
  Footprints,
  Gamepad2,
  Gem,
  Gift,
  Glasses,
  Globe,
  Hammer,
  HardDrive,
  Hash,
  Headphones,
  Heart,
  Hospital,
  Hotel,
  Hourglass,
  House,
  IceCreamCone,
  Inbox,
  Infinity as InfinityIcon,
  Key,
  Keyboard,
  Lamp,
  Landmark,
  Laptop,
  Leaf,
  Lightbulb,
  Lock,
  LockKeyhole,
  Mail,
  Map as MapIcon,
  MapPin,
  Medal,
  MessageCircle,
  MessageSquare,
  Monitor,
  Moon,
  Mountain,
  Mouse,
  Music,
  Newspaper,
  Notebook,
  Paintbrush,
  Palette,
  Pencil,
  PenTool,
  Percent,
  Phone,
  PhoneCall,
  Pizza,
  Plane,
  Printer,
  Puzzle,
  Rabbit,
  Radio,
  Rainbow,
  Receipt,
  Rocket,
  Sandwich,
  School,
  Scissors,
  Scroll,
  Search,
  Send,
  Server,
  Shield,
  ShieldCheck,
  Ship,
  Shirt,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Snowflake,
  Sofa,
  Soup,
  Sparkles,
  Speaker,
  Sprout,
  Squirrel,
  Star,
  Store,
  Sun,
  Sunrise,
  Sunset,
  Tag,
  Target,
  Tent,
  Terminal,
  ThumbsUp,
  Timer,
  TreeDeciduous,
  TreePalm,
  TreePine,
  Trees,
  Trophy,
  Truck,
  Turtle,
  Tv,
  Umbrella,
  User,
  Users,
  Utensils,
  UtensilsCrossed,
  Volleyball,
  Wallet,
  Warehouse,
  Watch,
  Waves,
  Wifi,
  Wind,
  Wine,
  Wrench,
  Zap,
} from "lucide-react";
import type { ComponentType } from "react";
import { createElement, useMemo, useState } from "react";
import { useGridListbox } from "../../../hooks/useGridListbox";
import { usePopoverPosition } from "../../../hooks/usePopoverPosition";
import { Input, type InputSize, PAD_X, SIZE_CLASSES } from "../Input";
import { LEADING_ICON_SIZE } from "./pickerSizes";

export interface IconPickerOption {
  value: string;
  label: string;

  icon: ComponentType<{ size?: number | string }>;
}

export interface IconPickerProps {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  options?: IconPickerOption[];
  placeholder?: string;
  disabled?: boolean;
  size?: InputSize;
}

const COLUMNS = 6;

function pascalToLabel(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");
}

function pascalToKebab(name: string): string {
  return pascalToLabel(name).toLowerCase().replace(/\s+/g, "-");
}

const ICON_COMPONENTS: Record<string, ComponentType<{ size?: number | string }>> = {
  AlarmClock,
  Anchor,
  Apple,
  Archive,
  Armchair,
  AtSign,
  Award,
  Baby,
  Backpack,
  Banknote,
  Battery,
  Bed,
  Beer,
  Bell,
  Bike,
  Bird,
  Bluetooth,
  Book,
  BookOpen,
  Bookmark,
  Briefcase,
  Bug,
  Building,
  Building2,
  Cake,
  Calendar,
  Camera,
  Car,
  Carrot,
  Castle,
  Cat,
  Cherry,
  Church,
  Clipboard,
  ClipboardList,
  Clock,
  Cloud,
  CloudRain,
  Code,
  Coffee,
  Coins,
  Compass,
  Cpu,
  CreditCard,
  Crown,
  Database,
  Diamond,
  Dice5,
  Dog,
  DollarSign,
  Droplet,
  Dumbbell,
  Egg,
  Eye,
  EyeOff,
  Factory,
  File,
  FileText,
  Files,
  Film,
  Fish,
  Flag,
  Flame,
  Flower,
  Flower2,
  Folder,
  FolderOpen,
  Footprints,
  Gamepad2,
  Gem,
  Gift,
  Glasses,
  Globe,
  Hammer,
  HardDrive,
  Hash,
  Headphones,
  Heart,
  Hospital,
  Hotel,
  Hourglass,
  House,
  IceCreamCone,
  Infinity: InfinityIcon,
  Inbox,
  Keyboard,
  Key,
  Lamp,
  Landmark,
  Laptop,
  Leaf,
  Lightbulb,
  Lock,
  LockKeyhole,
  Mail,
  Map: MapIcon,
  MapPin,
  Medal,
  MessageCircle,
  MessageSquare,
  Monitor,
  Moon,
  Mountain,
  Mouse,
  Music,
  Newspaper,
  Notebook,
  Paintbrush,
  Palette,
  Pencil,
  PenTool,
  Percent,
  Phone,
  PhoneCall,
  Pizza,
  Plane,
  Printer,
  Puzzle,
  Rabbit,
  Radio,
  Rainbow,
  Receipt,
  Rocket,
  Sandwich,
  School,
  Scissors,
  Scroll,
  Send,
  Server,
  Shield,
  ShieldCheck,
  Ship,
  ShoppingBag,
  ShoppingCart,
  Shirt,
  Smartphone,
  Snowflake,
  Sofa,
  Soup,
  Sparkles,
  Speaker,
  Sprout,
  Squirrel,
  Star,
  Store,
  Sun,
  Sunrise,
  Sunset,
  Tag,
  Target,
  Tent,
  Terminal,
  ThumbsUp,
  Timer,
  Trees,
  TreeDeciduous,
  TreePalm,
  TreePine,
  Trophy,
  Truck,
  Turtle,
  Tv,
  Umbrella,
  User,
  Users,
  Utensils,
  UtensilsCrossed,
  Volleyball,
  Wallet,
  Warehouse,
  Watch,
  Waves,
  Wifi,
  Wind,
  Wine,
  Wrench,
  Zap,
};

const CURATED_ICON_NAMES = [
  "House",
  "Building",
  "Building2",
  "Landmark",
  "MapPin",
  "Map",
  "Globe",
  "Mountain",
  "Trees",
  "TreePine",
  "Tent",
  "Hotel",
  "Store",
  "Warehouse",
  "Castle",
  "Church",
  "Hospital",
  "School",
  "Factory",
  "Ship",
  "Anchor",
  "Waves",
  "TreePalm",
  "Compass",

  "Briefcase",
  "Book",
  "BookOpen",
  "Camera",
  "Car",
  "Bike",
  "Plane",
  "Truck",
  "Gift",
  "Key",
  "Lock",
  "Lightbulb",
  "Umbrella",
  "Backpack",
  "ShoppingCart",
  "ShoppingBag",
  "Wallet",
  "Watch",
  "Headphones",
  "Laptop",
  "Smartphone",
  "Tv",
  "Radio",
  "Speaker",
  "Printer",
  "Scissors",
  "Hammer",
  "Wrench",
  "Paintbrush",
  "Palette",
  "Music",
  "Film",
  "Gamepad2",
  "Puzzle",
  "Dice5",
  "Glasses",
  "Shirt",
  "Crown",
  "Diamond",
  "Gem",
  "Sofa",
  "Lamp",
  "Bed",
  "Armchair",

  "File",
  "FileText",
  "Files",
  "Folder",
  "FolderOpen",
  "Clipboard",
  "ClipboardList",
  "Notebook",
  "Bookmark",
  "Archive",
  "Inbox",
  "Mail",
  "Send",
  "Scroll",
  "Newspaper",
  "Receipt",
  "PenTool",
  "Pencil",

  "Dumbbell",
  "Trophy",
  "Medal",
  "Target",
  "Footprints",
  "Volleyball",

  "User",
  "Users",
  "Baby",
  "Dog",
  "Cat",
  "Bird",
  "Rabbit",
  "Bug",
  "Squirrel",
  "Turtle",

  "Sun",
  "Moon",
  "Cloud",
  "CloudRain",
  "Snowflake",
  "Zap",
  "Wind",
  "Flower",
  "Flower2",
  "Leaf",
  "TreeDeciduous",
  "Sprout",
  "Droplet",
  "Flame",
  "Rainbow",
  "Sunrise",
  "Sunset",

  "Coffee",
  "Pizza",
  "Apple",
  "Cherry",
  "IceCreamCone",
  "Cake",
  "Utensils",
  "UtensilsCrossed",
  "Wine",
  "Beer",
  "Soup",
  "Sandwich",
  "Carrot",
  "Egg",
  "Fish",

  "Cpu",
  "HardDrive",
  "Wifi",
  "Bluetooth",
  "Server",
  "Database",
  "Code",
  "Terminal",
  "Monitor",
  "Keyboard",
  "Mouse",
  "Battery",
  "Rocket",

  "MessageCircle",
  "MessageSquare",
  "Phone",
  "PhoneCall",
  "AtSign",
  "Hash",
  "Bell",

  "Heart",
  "Star",
  "Shield",
  "ShieldCheck",
  "Award",
  "Flag",
  "Infinity",
  "Sparkles",
  "ThumbsUp",

  "Clock",
  "Timer",
  "Calendar",
  "Hourglass",
  "AlarmClock",

  "CreditCard",
  "DollarSign",
  "Tag",
  "Percent",
  "Banknote",
  "Coins",

  "LockKeyhole",
  "Eye",
  "EyeOff",
] as const;

const DEFAULT_ICON_OPTIONS: IconPickerOption[] = CURATED_ICON_NAMES.map((name) => ({
  value: pascalToKebab(name),
  label: pascalToLabel(name),
  icon: ICON_COMPONENTS[name],
}));

export function IconPicker({
  label,
  value,
  onChange,
  options = DEFAULT_ICON_OPTIONS,
  placeholder = "Select icon",
  disabled = false,
  size = "lg",
}: Readonly<IconPickerProps>) {
  const [query, setQuery] = useState("");

  const filteredOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return options;
    return options.filter((option) => option.value.includes(trimmed) || option.label.toLowerCase().includes(trimmed));
  }, [options, query]);

  const {
    isOpen,
    setIsOpen,
    highlightedIndex,
    selectedIndex,
    containerRef,
    triggerRef,
    optionRefs,
    listboxId,
    optionId,
    openAt,
    commitSelection,
    handleTriggerKeyDown,
  } = useGridListbox({
    id: label ?? "icon-picker",
    options: filteredOptions,
    value,
    onChange,
    columns: COLUMNS,
    disabled,
  });

  const { panelRef, style: panelStyle } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef: triggerRef,
  });

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div ref={containerRef} className="relative isolate">
      {label && (
        <div id={`${listboxId}-label`} className="mb-1.5 text-sm font-medium text-content-secondary">
          {label}
        </div>
      )}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={isOpen ? optionId(highlightedIndex) : undefined}
        aria-label={label ? undefined : (selectedOption?.label ?? placeholder)}
        aria-labelledby={label ? `${listboxId}-label` : undefined}
        onClick={() => {
          if (isOpen) {
            setIsOpen(false);
          } else {
            setQuery("");
            openAt(selectedIndex >= 0 ? selectedIndex : 0);
          }
        }}
        onKeyDown={handleTriggerKeyDown}
        className={[
          "btn-focus-ring flex w-full items-center gap-2 border bg-surface-elevated text-left transition-colors",
          SIZE_CLASSES[size],
          PAD_X[size].start,
          PAD_X[size].end,
          isOpen ? "border-border-focus" : "border-border-input hover:border-border-strong",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        ].join(" ")}
      >
        {selectedOption ? (
          createElement(selectedOption.icon, { size: LEADING_ICON_SIZE[size] })
        ) : (
          <Circle size={LEADING_ICON_SIZE[size]} className="text-content-tertiary" />
        )}
        <span
          className={[
            "flex-1 truncate text-sm leading-none pb-0.5",
            selectedOption ? "text-content-primary" : "text-content-tertiary",
          ].join(" ")}
        >
          {selectedOption?.label ?? placeholder}
        </span>
      </button>
      {isOpen && (
        <div
          ref={panelRef}
          role="listbox"
          id={listboxId}
          aria-label={label ?? "Icon"}
          style={panelStyle}
          className="z-[110] w-72 rounded-lg border border-border-input bg-surface-elevated p-3 shadow-lg"
        >
          <div className="mb-2">
            <Input
              size="sm"
              leadingIcon={<Search />}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search icons"
            />
          </div>
          <div className="grid h-64 grid-cols-6 content-start gap-1 overflow-y-auto">
            {filteredOptions.map((option, index) => (
              <button
                key={option.value}
                id={optionId(index)}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                type="button"
                role="option"
                aria-selected={index === selectedIndex}
                title={option.label}
                onMouseEnter={() => openAt(index)}
                onClick={() => commitSelection(index)}
                className={[
                  "grid cursor-pointer place-items-center rounded-lg p-2 text-content-secondary transition-colors",
                  index === selectedIndex
                    ? "bg-brand-primary-subtle text-brand-primary-on-subtle"
                    : index === highlightedIndex
                      ? "bg-surface-base"
                      : "hover:bg-surface-base",
                ].join(" ")}
              >
                {createElement(option.icon, { size: 20 })}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="col-span-6 py-6 text-center text-sm text-content-tertiary">No icons found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
